/**
 * Loads support detail content for the product referenced by ?model=.
 *
 * The product-support-page section on /pages/support-detail is rendered hidden.
 * This module reads the `model` query parameter, fetches that product's support
 * section through the Section Rendering API (rendered in the product's context,
 * so its metafields auto-fill), and swaps it into the page. It also wires the
 * in-page anchor navigation (specs / manuals / app / FAQ) after injection.
 *
 * The page it drives can be entered repeatedly through soft navigation, so the
 * whole setup is re-runnable: every pass binds a freshly swapped root element,
 * and the section observer of a previous visit is disconnected first.
 */
(() => {
  if (window.Shopify?.designMode === true) return;

  let observer = null;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char])
    );

  const showState = (content, root, supportCenterUrl, strings, title, body) => {
    content.innerHTML = [
      '<div class="psp-empty-state">',
      `  <h1 class="psp-empty-state__title">${escapeHtml(title)}</h1>`,
      `  <p class="psp-empty-state__body">${escapeHtml(body)}</p>`,
      `  <a class="psp-button psp-button--primary" href="${escapeHtml(supportCenterUrl)}">${escapeHtml(strings.backLink)}</a>`,
      '</div>',
    ].join('');
    root.hidden = false;
  };

  const initNavigation = (root) => {
    const navigationLinks = [...root.querySelectorAll('[data-support-anchor]')];
    const contentSections = [...root.querySelectorAll('[data-support-section]')];

    const setActiveLink = (sectionId) => {
      navigationLinks.forEach((link) => {
        const isActive = link.getAttribute('href') === `#${sectionId}`;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    navigationLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = root.querySelector(link.getAttribute('href'));
        if (!target) return;

        event.preventDefault();
        setActiveLink(target.id);
        const scrollToTarget = () =>
          target.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start',
          });
        if (!prefersReducedMotion() && document.startViewTransition) {
          document.startViewTransition(scrollToTarget);
        } else {
          scrollToTarget();
        }
        history.replaceState(null, '', link.getAttribute('href'));
      });
    });

    observer?.disconnect();
    observer = new IntersectionObserver(
      () => {
        const viewportAnchor = window.innerHeight * 0.25;
        const visibleSection = contentSections
          .map((section) => ({ section, bounds: section.getBoundingClientRect() }))
          .filter(({ bounds }) => bounds.bottom > viewportAnchor && bounds.top < window.innerHeight)
          .sort((a, b) => Math.abs(a.bounds.top - viewportAnchor) - Math.abs(b.bounds.top - viewportAnchor))[0];
        if (visibleSection) setActiveLink(visibleSection.section.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: [0, 0.15, 0.35] }
    );

    contentSections.forEach((section) => observer.observe(section));
  };

  const init = () => {
    const root = document.querySelector('[data-support-detail-root]');
    if (!root) return;

    const content = root.querySelector('[data-support-detail-content]');
    if (!content) return;

    if (root.dataset.navInited === 'true') return;
    root.dataset.navInited = 'true';

    const supportCenterUrl =
      root.dataset.supportCenterUrl || `${window.Shopify.routes.root}pages/support`;
    const strings = {
      emptyTitle: root.dataset.emptyTitle || 'Select a product',
      emptyBody: root.dataset.emptyBody || 'Choose your Catsights product from the support center.',
      notFoundTitle: root.dataset.notFoundTitle || "We couldn't find support for that product",
      notFoundBody: root.dataset.notFoundBody || 'No support information is available for this product yet.',
      backLink: root.dataset.backLink || 'Back to Support Center',
    };

    const requestedModel = new URLSearchParams(window.location.search).get('model')?.trim();

    if (!requestedModel) {
      showState(content, root, supportCenterUrl, strings, strings.emptyTitle, strings.emptyBody);
      return;
    }

    const baseUrl = window.Shopify.routes.root;
    const sectionUrl = `${baseUrl}products/${encodeURIComponent(requestedModel)}?section_id=product-support-page`;

    fetch(sectionUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed with ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('div');
        template.innerHTML = html;
        const fetchedContent = template.querySelector('[data-support-detail-content]');
        if (!fetchedContent) throw new Error('Support content not found');
        content.innerHTML = fetchedContent.innerHTML;
        root.hidden = false;
        initNavigation(root);
      })
      .catch(() => {
        showState(content, root, supportCenterUrl, strings, strings.notFoundTitle, strings.notFoundBody);
      });
  };

  init();
  document.addEventListener('site:navigation:end', init);
})();
